import QuantitySelector from '@/components/common/Forms/QuantitySelector'
import useAppContext from '@/hooks/useAppContext'
import useStoreItemAvailability from '@/hooks/useStoreItemAvailability'
import { selectDetailMenuSite } from '@/redux/meta/selector'
import { useTranslate } from '@tolgee/react'
import { cn } from 'oddle-shop-monorepo.design-system'
import { memo, ReactNode } from 'react'
import { useSelector } from 'react-redux'
import { getProductButtonProps } from 'src/utils/product'
import Button from '@/components/common/Button'
import { useFormContext } from 'react-hook-form'

interface AddToCartProps {
  formatMoney?: (x: number) => number
  totalQuantity: number | null
  totalItemPrice: number | null
  productState: Oms.MenuItem
  category: Oms.MenuCategory
}

export const AddToCartElement = ({
  disabled = false,
  buttonText = '',
  withInput = false,
  loading = false,
}: {
  disabled?: boolean
  buttonText?: string
  withInput?: boolean
  loading?: boolean
}) => {
  const { formState } = useFormContext()

  return (
    <>
      <QuantitySelector name="totalQuantity" min={1} withInput={withInput} />
      <Button
        type="submit"
        disabled={disabled || loading || formState.isSubmitting}
        className={cn('ml-5 w-full', disabled || loading ? 'opacity-80' : '')}
      >
        {buttonText}
      </Button>
    </>
  )
}

const AddToCart = memo(
  ({
    totalItemPrice,
    totalQuantity,
    productState: product,
    category,
  }: AddToCartProps): ReactNode => {
    const { t } = useTranslate()
    const { formatMoney } = useAppContext()
    const metaState = useSelector(selectDetailMenuSite)
    const { enableTimeLimitUnavailability } = metaState?.features || {}
    const {
      text: productButtonText,
      disabled,
      loading,
    } = getProductButtonProps(
      t,
      product,
      category,
      useStoreItemAvailability({ product, category }),
      Boolean(enableTimeLimitUnavailability),
      metaState?.menu?.gmtOffset
    )
    const totalPrice = (totalItemPrice ?? 0) * (totalQuantity ?? 0)

    return (
      <AddToCartElement
        withInput
        disabled={!totalQuantity || disabled}
        buttonText={
          disabled
            ? String(productButtonText)
            : String(
                t(`Add {totalPrice}`, { totalPrice: formatMoney(totalPrice) })
              )
        }
        loading={loading}
      />
    )
  }
)

AddToCart.displayName = 'AddToCart'

export default AddToCart
